import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, Check, X } from 'lucide-react';

const SetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { setPassword: setPasswordAPI } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Password requirements check
  const passwordChecks = useMemo(() => ({
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*]/.test(password),
  }), [password]);

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error('La password non soddisfa tutti i requisiti');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    setLoading(true);

    try {
      await setPasswordAPI(token, password);
      setSuccess(true);
      toast.success('Password impostata con successo!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      console.error('Set password error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'impostazione della password');
    } finally {
      setLoading(false);
    }
  };

  // Componente per mostrare un requisito
  const RequirementItem = ({ met, label }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-slate-400'}`}>
      {met ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-slate-300" />
      )}
      <span>{label}</span>
    </div>
  );

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5FBF9] p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Link non valido</h2>
          <p className="text-slate-600 mb-6">Il link utilizzato non è valido o è scaduto.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold py-2 px-6 rounded-lg"
          >
            Torna al Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5FBF9] p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Password impostata!</h2>
          <p className="text-slate-600">Reindirizzamento al login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5FBF9] p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_info-security-fdl/artifacts/0n58s6iv_LOGO_e-CAA_light.png"
              alt="e-CAA Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Imposta Password</h1>
            <p className="text-slate-500">Scegli una password sicura per il tuo account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="set-password-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nuova Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#3BA99C] focus:border-transparent transition-colors ${
                  password.length > 0 
                    ? isPasswordValid 
                      ? 'border-green-300 bg-green-50/30' 
                      : 'border-amber-300 bg-amber-50/30'
                    : 'border-slate-200'
                }`}
                placeholder="Inserisci la password"
                data-testid="password-input"
              />
              
              {/* Requisiti password */}
              <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-1.5">
                <p className="text-xs font-medium text-slate-500 mb-2">La password deve contenere:</p>
                <RequirementItem met={passwordChecks.minLength} label="Almeno 8 caratteri" />
                <RequirementItem met={passwordChecks.hasUppercase} label="Almeno una lettera maiuscola (A-Z)" />
                <RequirementItem met={passwordChecks.hasLowercase} label="Almeno una lettera minuscola (a-z)" />
                <RequirementItem met={passwordChecks.hasNumber} label="Almeno un numero (0-9)" />
                <RequirementItem met={passwordChecks.hasSpecial} label="Almeno un carattere speciale (!@#$%^&*)" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Conferma Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#3BA99C] focus:border-transparent transition-colors ${
                  confirmPassword.length > 0 
                    ? passwordsMatch 
                      ? 'border-green-300 bg-green-50/30' 
                      : 'border-red-300 bg-red-50/30'
                    : 'border-slate-200'
                }`}
                placeholder="Ripeti la password"
                data-testid="confirm-password-input"
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  Le password non corrispondono
                </p>
              )}
              {passwordsMatch && (
                <p className="mt-2 text-sm text-green-500 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Le password corrispondono
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !isPasswordValid || !passwordsMatch}
              className="w-full bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Impostazione...
                </>
              ) : (
                'Imposta Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
